import { OPMEvidenceData, useOpmEvidences } from "@/api/opm-evidence";
import {
  OPMEvidenceValidationPayload,
  useOPMEvidenceValidate,
  useRFS,
  useRFSStatusUpdate,
} from "@/api/rfs";
import { FieldFile } from "@/components/form/field-file";
import { FormContainer } from "@/components/form/form-container";
import { RequiredAsterisk } from "@/components/form/required-asterisk";
import { SubmitButton } from "@/components/ui/button";
import { ContainerPrimary } from "@/features/rfs/document/ui";
import { MISSING_ID_IN_ROUTE, ROLE } from "@/utils/constant";
import { swalError, swalErrorDefault, swalSuccess } from "@/utils/lib/swal";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { useParams } from "react-router-dom";
import buildEvidenceErrorMessage from "../utils/buildEvidenceErrorMessage";
import { useValidation } from "../contexts/validationContext";
import ValidationIcon from "@/components/shared/ValidationIcon";
import { useCurrentUser } from "@/features/auth/useCurrentUser";
import { hasCommonElement } from "@/utils/helper/hasCommonElement";
import { createRFSStatusUpdater } from "../utils/rfsStatusUpdater";
import {
  overrideEvidenceValid,
  overrideEvidenceInvalid,
} from "../utils/dataMappers";
import { OverallStatus } from "../providers/ValidationProvider";

export default function ClampHookEvidenceHandler({
  clampHookEvidence,
}: {
  clampHookEvidence?: OPMEvidenceData;
}) {
  const { id } = useParams();
  const { roles } = useCurrentUser();
  if (!id) {
    throw new Error(MISSING_ID_IN_ROUTE);
  }
  const rfsId = id;

  const isViewOnly = hasCommonElement(roles, [ROLE.VENDOR_VIEW]);

  const [errors, setErrors] = useState<string | undefined>(undefined);
  const { mutate: submit, isPending } = useOPMEvidenceValidate();
  const {
    requiredList,
    overallStatus: currentOverallStatus,
    getStatusForSubmit,
  } = useValidation();
  const { data: rfs } = useRFS({ id: rfsId });
  const { data: evidences } = useOpmEvidences({ rfsId });
  const { mutate: updateStatus } = useRFSStatusUpdate();

  const form = useForm();

  const currentFile = form.watch("clampHookEvidenceMedia");

  const path = currentFile?.path || clampHookEvidence?.opmEvidencePath;
  const isRequired = requiredList.some(
    (validation) => validation.code === "A16",
  );

  function handleStatus(nextStatus: OverallStatus) {
    const statusUpdater = createRFSStatusUpdater({
      rfsId,
      overallStatus: nextStatus,
      rfsData: rfs,
      updateStatusMutation: updateStatus,
      prefix: "opm",
      options: {
        onError: () => {
          swalErrorDefault.fire({
            title: "Status Update Failed",
            text: "You failed to update the RFS status. Please try again.",
          });
        },
      },
    });
    statusUpdater();
  }

  function handleSumit() {
    if ((!currentFile.id && !clampHookEvidence?.id) || !rfsId) {
      return;
    }

    const payload: OPMEvidenceValidationPayload = {
      opmEvidence: {
        opmEvidenceType: "clamphook",
        opmEvidenceMedia: {
          id: currentFile?.id ?? clampHookEvidence?.id,
          path: currentFile?.path ?? clampHookEvidence?.opmEvidencePath,
          originalFileName:
            currentFile.originalFileName ??
            clampHookEvidence?.opmEvidenceFilename,
        },
      },
    };

    if (clampHookEvidence?.id) {
      payload.opmEvidence.id = clampHookEvidence.id;
    }

    submit(
      { id: rfsId, data: payload },
      {
        onSuccess() {
          setErrors(undefined);
          swalSuccess.fire({
            title: "Validation Successfull",
            text: "You have successfully validated the Clamp Hook Evidence (zip).",
          });
          const nextStatus =
            getStatusForSubmit?.({
              opmEvidences: overrideEvidenceValid(evidences, "clamphook"),
            })?.overallStatus ?? currentOverallStatus;
          handleStatus(nextStatus);
        },
        onError(error) {
          setErrors(undefined);
          const apiError = error as {
            response?: { status?: number; data?: { data?: unknown } };
          };
          const errors = apiError?.response?.data?.data;
          const normalizedErrors = Array.isArray(errors) ? errors : [];
          const errorText = buildEvidenceErrorMessage(normalizedErrors);
          const errorMessage =
            errorText ?? "There was an error with the uploaded file.";
          setErrors(errorMessage);

          // Submit ditolak: cermin dari onSuccess — tambal data secara lokal lalu
          // hitung ulang, tanpa menunggu refetch.
          if (apiError?.response?.status === 400) {
            const nextStatus =
              getStatusForSubmit?.({
                opmEvidences: overrideEvidenceInvalid(evidences, "clamphook"),
              })?.overallStatus ?? currentOverallStatus;
            handleStatus(nextStatus);
          }

          swalError.fire({
            title: "Validation Failed",
            text: errorMessage,
          });
        },
      },
    );
  }

  return (
    <ContainerPrimary className="grid grid-cols-12">
      <span className="col-span-4  whitespace-nowrap">
        <div className="flex gap-2">
          <div className="flex flex-row">
            <span>
              Clamp Hook Evidence (zip){isRequired && <RequiredAsterisk />}{" "}
            </span>
            <ValidationIcon value={clampHookEvidence?.isValid} />
          </div>
        </div>
      </span>

      <div className="col-span-8">
        <FormContainer
          form={form}
          id="olt-evidence-form"
          className="flex items-center justify-end space-x-1"
        >
          <div className="flex-1">
            <FieldFile
              hideLabel
              name="clampHookEvidenceMedia"
              label="Clamp Hook Evidence"
              media={{
                target: "/opm/evidence",
                entityId: id,
                type: "clampHook",
              }}
              canDownloadExistingFile={!!path}
              pathExistingFile={path}
              defaultName={clampHookEvidence?.opmEvidenceFilename}
              accept=".zip"
              isDisabled={isViewOnly}
            />
          </div>
          <div className="w-min flex-none">
            <SubmitButton
              loading={isPending}
              onClick={handleSumit}
              disabled={!path || isViewOnly}
            />
          </div>
        </FormContainer>
      </div>

      {errors && (
        <div className="col-span-12">
          <span className="text-sm text-red-500">{errors}</span>
        </div>
      )}
    </ContainerPrimary>
  );
}
